import { useMemo } from 'react';
import { css } from '@emotion/react';
import { PhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { TextLink, Heading, Table, Chip, Text } from '@frontend/design-system';
import { trackingId } from '../../../tracking';
import { sortRoutePhoneNumbers } from '../helpers/dialables';
import { ExtendedCallRoute } from '../types';

type DeviceExtensionRouteNumbersProps = {
  callRoute: ExtendedCallRoute;
  hasChildLocations: boolean;
  isLoading?: boolean;
  onEditPhoneNumbers: (callRouteId: string) => void;
  onRemovePhoneNumber: (phoneNumberId: string) => void;
};

export const DeviceExtensionRouteNumbers = ({
  callRoute,
  hasChildLocations,
  isLoading = false,
  onEditPhoneNumbers,
  onRemovePhoneNumber,
}: DeviceExtensionRouteNumbersProps) => {
  const { t } = useTranslation('phone');
  const { getScopeName } = useAppScopeStore();

  const { navigate: settingsNavigate } = useSettingsNavigate();

  const sortedPhoneNumbers = useMemo(() => sortRoutePhoneNumbers(callRoute.phoneNumbers), [callRoute.phoneNumbers]);

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <section>
        <Heading level={3}>{t('Extension')}</Heading>

        <Trans t={t}>
          <Text>
            This extension can be managed in{' '}
            {!!callRoute.deviceExtension?.deviceId ? (
              <TextLink
                onClick={() => {
                  settingsNavigate({
                    to: `/phone/devices/:id`,
                    params: { id: callRoute.deviceExtension?.deviceId },
                  });
                }}
                trackingId={trackingId({
                  context: 'setting',
                  feature: 'device-extension-route',
                  details: 'numbers-panel-device-link',
                })}
              >
                Device Settings
              </TextLink>
            ) : (
              <>{t('Device Settings')}</>
            )}
            .
          </Text>
        </Trans>

        <Text
          css={css`
            margin-top: ${theme.spacing(3)};
          `}
        >
          {callRoute.extensionNumbers?.[0] || '--'}
        </Text>
      </section>

      <div
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: theme.spacing(2),
        }}
      >
        <Heading level={3}>{t('Phone Numbers')}</Heading>
        <TextLink
          onClick={() => onEditPhoneNumbers(callRoute.callRouteId)}
          weight='bold'
          css={{ marginBottom: theme.spacing(2) }}
          trackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'side-panel-edit-phone-numbers-link',
          })}
        >
          {t('Assign Phone Numbers')}
        </TextLink>
      </div>

      {sortedPhoneNumbers.length > 0 ? (
        <Table
          data={sortedPhoneNumbers}
          globalTrackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'side-panel-numbers-table',
          })}
          colConfig={[
            {
              Header: t('Phone Number'),
              accessor: (phoneNumber) => phoneNumber.phoneNumber,
              id: 'phone-number',
              cellRenderer: (phoneNumber) => formatPhoneNumber(phoneNumber),
              width: 180,
            },
            {
              id: 'phoneNumberName',
              Header: t('Name'),
              accessor: (item: PhoneNumber) => (item.displayName ? item.displayName : '--'),
              width: 180,
            },
            {
              Header: t('Location'),
              accessor: (phoneNumber) => phoneNumber.locationId,
              cellRenderer: (locationId) =>
                locationId ? <Chip.SingleChip>{getScopeName(locationId)}</Chip.SingleChip> : '',
              id: 'location-name',
              sortType: 'caseInsensitive',
              width: 190,
              omit: !hasChildLocations,
            },
          ]}
          rowActions={{
            actions: [
              {
                label: t('Remove phone number'),
                Icon: () => <Icon name='remove2' />,
                onClick: (phoneNumber) => onRemovePhoneNumber(phoneNumber.id),
                disabled: isLoading,
              },
            ],
          }}
        />
      ) : (
        <Text>{t('Assign a phone number, if this device extension needs direct dialing.')}</Text>
      )}
    </div>
  );
};
